<template>
  <div class="mx-4">
    <!-- Filtros -->
    <v-row no-gutters class="mt-6">
      <v-col cols="12">
        <app-title texto="FILTROS" />
      </v-col>

      <!-- Filtro de Unidades -->
      <v-col cols="12" md="6">
        <select-multiple-unidades
          v-model="filtro.unidades"
          :mapeamento-id="diagnostico ? diagnostico.mapeamento.id : null"
        />
      </v-col>

      <!-- Filtro de Motivos -->
      <v-col cols="12" md="6">
        <v-autocomplete
          v-model="filtro.motivos"
          :items="motivos"
          item-text="descricao"
          class="mx-2"
          label="Motivo"
          prepend-icon="library_books"
          placeholder="Digite para pesquisar..."
          no-results-text="Nenhum resultado encontrado."
          required
          return-object
          multiple
          small-chips
          deletable-chips
          @change="pesquisa.motivo = ''"
        />
      </v-col>
    </v-row>

    <!-- Dispensas do Diagnóstico -->
    <v-row no-gutters class="mt-6">
      <!-- Título -->
      <v-col cols="12">
        <app-title :texto="tituloTabelaDispensas" />
      </v-col>

      <!-- Tabela -->
      <v-col cols="12">
        <v-data-table
          v-if="diagnostico"
          :items="dispensasView"
          :headers="headers"
          :search="opcoes.pesquisa"
          :sort-by="opcoes.ordenarPor"
          :sort-desc="opcoes.decrescente"
          :custom-filter="customFilter"
          item-key="id"
        >
          <!-- Pesquisa e Ordenação -->
          <template v-slot:top>
            <v-row dense>
              <v-col cols="12" md="4">
                <app-sort v-model="opcoes" />
              </v-col>
              <v-col cols="0" md="4" />
              <v-col cols="12" md="4">
                <app-search v-model="opcoes" />
              </v-col>
            </v-row>
          </template>

          <!-- Númeração das linhas -->
          <template v-slot:[`item.n`]="{ index }">{{ ++index }}</template>

          <!-- Nome do Servidor Dispensado -->
          <template v-slot:[`item.servidor`]="{ item }">
            {{ item.usuario.nome }}

            <!-- Dispensa importada via integração --->
            <v-chip
              v-if="item.fonte"
              class="ml-2"
              color="success"
              outlined
              x-small
            >
              Importado
            </v-chip>
          </template>

          <!-- Botões de Ação -->
          <template v-slot:[`item.acoes`]="{ item }">
            <!-- Editar -->
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-btn
                  v-if="diagnostico.status === 'FUTURO' && !item.fonte"
                  icon
                  small
                  color="secondary"
                  class="mr-2"
                  v-on="on"
                  @click="editar(item)"
                >
                  <v-icon>edit</v-icon>
                </v-btn>
              </template>
              <span>Clique no botão para editar a dispensa</span>
            </v-tooltip>

            <!-- Excluir -->
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-btn
                  v-if="diagnostico.status === 'FUTURO'"
                  icon
                  small
                  color="error"
                  v-on="on"
                  @click="confirmarExclusao(item)"
                >
                  <v-icon>delete</v-icon>
                </v-btn>
              </template>
              <span>Clique no botão para excluir a dispensa</span>
            </v-tooltip>
          </template>
        </v-data-table>
      </v-col>
    </v-row>

    <!-- Janela de Dialogo para confirmação da exclusão -->
    <dialog-remove
      v-show="dialogRemove.visibilidade"
      :confirm-action="excluir"
      :dialog-visible="dialogRemove.visibilidade"
      :toogle-visible="toogleDialogRemove"
      descricao="Você realmente deseja excluir essa dispensa?"
    />
  </div>
</template>

<script>
import { RepositoryFactory } from '@/repositories/RepositoryFactory';
import { pesquisar } from '@/modules/texto';
import AppTitle from '@/components/core/AppTitle';
import SelectMultipleUnidades from '@/components/SelectMultipleUnidades';
import AppSort from '@/components/core/AppSort';
import AppSearch from '@/components/core/AppSearch';
import DialogRemove from '@/components/DialogRemove';

const dispensaDiagnosticoRepository = RepositoryFactory.get('dispensaDiagnostico');

export default {
  name: 'DispensaList',

  components: {
    AppTitle,
    SelectMultipleUnidades,
    AppSort,
    AppSearch,
    DialogRemove,
  },

  props: {
    diagnostico: {
      type: Object,
      required: true,
    },
    dispensas: {
      type: Array,
      required: true,
      default: () => ([]),
    },
    motivos: {
      type: Array,
      required: true,
      default: () => ([]),
    },
  },

  data() {
    return {
      headers: [
        {
          text: 'Nº',
          value: 'n',
          sortable: false,
          width: '2%',
        },
        {
          text: 'SERVIDOR',
          value: 'servidor',
          sortable: false,
          width: '38%',
        },
        {
          text: 'UNIDADE',
          value: 'usuario.unidade.sigla',
          width: '15%',
          sortable: false,
          align: 'center',
        },
        {
          text: 'MOTIVO',
          value: 'motivo.descricao',
          sortable: false,
          width: '30%',
        },
        {
          text: '',
          value: 'acoes',
          sortable: false,
          width: '15%',
          align: 'end',
        },
      ],
      opcoes: {
        pesquisa: '',
        decrescente: false,
        ordenarPor: 'usuario.nome',
        selecionado: { id: 'usuario.nome', descricao: 'Servidor' },
        lista: [
          { id: 'usuario.nome', descricao: 'Servidor' },
          { id: 'usuario.unidade.sigla', descricao: 'Unidade' },
          { id: 'motivo.descricao', descricao: 'Motivo' },
        ],
      },
      filtro: {
        unidades: {
          isTodasSelecionadas: null,
          selecionado: [],
          visualizacao: '',
        },
        motivos: [],
      },
      pesquisa: {
        motivo: '',
      },
      dialogRemove: { visibilidade: false, item: null },
    };
  },

  computed: {
    // Dispensas filtradas de acordo com os filtros selecionados
    dispensasView() {
      return this.dispensas
        .filter((d) => (
          this.filtro.unidades.selecionado.length === 0
          || this.filtro.unidades.selecionado.some((un) => un.id === d.usuario.unidade.id)
        ) && (this.filtro.motivos.length === 0 || this.filtro.motivos.some((m) => m.id === d.motivo.id)))
        .map((d) => ({
          id: `${d.diagnostico.id}-${d.usuario.id}`,
          ...d,
        }));
    },
    tituloTabelaDispensas() {
      let titulo = 'DISPENSAS DO DIAGNÓSTICO';
      if (this.diagnostico) {
        titulo += ` (${this.diagnostico.nome})`;
      }
      return titulo;
    },
  },

  watch: {
    motivos(value) {
      // Remove do filtro caso o motivo seja deletado
      this.filtro.motivos = this.filtro.motivos.filter((m) => value.some((m2) => m2.id === m.id));
    },
  },

  methods: {
    editar(item) {
      this.$emit('update', item);
    },
    confirmarExclusao(item) {
      this.$emit('reset');
      this.dialogRemove.item = { ...item };
      this.toogleDialogRemove();
    },
    toogleDialogRemove() {
      this.dialogRemove.visibilidade = !this.dialogRemove.visibilidade;
      if (!this.dialogRemove.visibilidade) {
        this.dialogRemove.item = null;
      }
    },
    excluir() {
      if (this.dialogRemove.item) {
        const diagnosticoId = this.dialogRemove.item.diagnostico.id;
        const usuarioId = this.dialogRemove.item.usuario.id;

        dispensaDiagnosticoRepository.delete(diagnosticoId, usuarioId).then((response) => {
          this.$emit('delete', this.dialogRemove.item);
          this.toogleDialogRemove();
          this.$store.dispatch('app/success', {
            response,
            message: 'Dispensa excluída com sucesso!',
          });
        });
      }
    },
    customFilter(value, search, item) {
      return pesquisar(item.usuario.nome, search) || pesquisar(item.usuario.unidade.sigla, search)
        || pesquisar(item.motivo.descricao, search);
    },
  },
};
</script>
